import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ThoughtBubbleFlow } from '../types/thought-bubble';

const STORAGE_KEY = 'tio-thought-bubble-flows';

export default class ThoughtBubbleStorageService extends Service {
  @tracked _revision = 0;

  private _getFlows(): ThoughtBubbleFlow[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ThoughtBubbleFlow[]) : [];
    } catch {
      return [];
    }
  }

  private _saveFlows(flows: ThoughtBubbleFlow[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flows));
    this._revision++;
  }

  get allFlows(): ThoughtBubbleFlow[] {
    // Access _revision to make this reactive
    void this._revision;
    return this._getFlows();
  }

  getAllFlows(): ThoughtBubbleFlow[] {
    return this.allFlows;
  }

  getFlow(id: string): ThoughtBubbleFlow | undefined {
    return this._getFlows().find((f) => f.id === id);
  }

  getActiveFlows(): ThoughtBubbleFlow[] {
    return this._getFlows().filter((f) => f.status === 'active');
  }

  saveFlow(flow: ThoughtBubbleFlow): void {
    const flows = this._getFlows();
    const index = flows.findIndex((f) => f.id === flow.id);
    const now = new Date().toISOString();
    const updated = { ...flow, updatedAt: now };

    if (index >= 0) {
      flows[index] = updated;
    } else {
      flows.push({ ...updated, createdAt: now });
    }

    this._saveFlows(flows);
  }

  deleteFlow(id: string): void {
    const flows = this._getFlows().filter((f) => f.id !== id);
    this._saveFlows(flows);
  }

  updateFlowStatus(id: string, status: ThoughtBubbleFlow['status']): void {
    const flows = this._getFlows();
    const flow = flows.find((f) => f.id === id);
    if (flow) {
      flow.status = status;
      flow.updatedAt = new Date().toISOString();
      this._saveFlows(flows);
    }
  }

  duplicateFlow(id: string): ThoughtBubbleFlow | undefined {
    const original = this.getFlow(id);
    if (!original) return undefined;

    const copy: ThoughtBubbleFlow = {
      ...structuredClone(original),
      id: crypto.randomUUID(),
      name: `${original.name} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const flows = this._getFlows();
    flows.push(copy);
    this._saveFlows(flows);
    return copy;
  }

  exportFlows(): string {
    return JSON.stringify(this._getFlows(), null, 2);
  }

  importFlows(json: string): { count: number } {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('Invalid JSON: unable to parse flows');
    }

    if (!Array.isArray(parsed)) {
      throw new Error('Invalid format: expected an array of flows');
    }

    const imported = parsed as Record<string, unknown>[];
    const now = new Date().toISOString();

    // Normalize/validate each flow to ensure required fields exist
    const normalized: ThoughtBubbleFlow[] = imported.map((item) => {
      const flow = { ...item };
      if (typeof flow['id'] !== 'string' || !flow['id']) {
        flow['id'] = crypto.randomUUID();
      }
      if (typeof flow['status'] !== 'string' || !flow['status']) {
        flow['status'] = 'draft';
      }
      if (typeof flow['createdAt'] !== 'string') {
        flow['createdAt'] = now;
      }
      if (typeof flow['updatedAt'] !== 'string') {
        flow['updatedAt'] = now;
      }
      if (
        !flow['targetingRules'] ||
        typeof flow['targetingRules'] !== 'object' ||
        !Array.isArray((flow['targetingRules'] as Record<string, unknown>)['rules'])
      ) {
        flow['targetingRules'] = { rules: [], matchMode: '', firstLoginOnly: false };
      }
      if (!Array.isArray(flow['enabledDomains'])) {
        flow['enabledDomains'] = [];
      }
      return flow as unknown as ThoughtBubbleFlow;
    });

    const existing = this._getFlows();
    const existingIds = new Set(existing.map((f) => f.id));

    for (const flow of normalized) {
      if (existingIds.has(flow.id)) {
        const idx = existing.findIndex((f) => f.id === flow.id);
        if (idx >= 0) existing[idx] = flow;
      } else {
        existing.push(flow);
      }
    }

    this._saveFlows(existing);
    return { count: normalized.length };
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-storage': ThoughtBubbleStorageService;
  }
}
